import { query } from "../erd-editor-schema/dist/query/index.js";
import { DictionaryKind } from "./modules/dictionary/actions.js";
import { isListed } from "./modules/dictionary/membership.js";
import { ActionType } from "./modules/table-column/actions.js";
import { changeColumnDomainAction } from "./modules/table-column/atom.actions.js";
import { domainTypeIn, toDomainTypeActions, toDomainUnlinkActions } from "./modules/dictionary/domainTypes.js";
import { dictionaryPushUndoHistoryMap, reverseDictionaryListings } from "./modules/dictionary/history.js";
import { ActionType as ActionType$1 } from "./modules/editor/actions.js";
import { keepsDictionaries } from "./modules/editor/atom.actions.js";
import { editorPushUndoHistoryMap } from "./modules/editor/history.js";
import { indexPushUndoHistoryMap } from "./modules/index/history.js";
import { indexColumnPushUndoHistoryMap } from "./modules/index-column/history.js";
import { memoPushStreamHistoryMap, memoPushUndoHistoryMap } from "./modules/memo/history.js";
import { relationshipPushUndoHistoryMap } from "./modules/relationship/history.js";
import { settingsPushStreamHistoryMap, settingsPushUndoHistoryMap } from "./modules/settings/history.js";
import { ActionType as ActionType$2 } from "./modules/table/actions.js";
import { tablePushStreamHistoryMap, tablePushUndoHistoryMap } from "./modules/table/history.js";
import { tableColumnPushUndoHistoryMap } from "./modules/table-column/history.js";
import { tableGroupPushStreamHistoryMap, tableGroupPushUndoHistoryMap } from "./modules/table-group/history.js";
import { safeCallback } from "../utils/safeCallback.js";
import { cloneDeep, isString } from "es-toolkit";
//#region src/engine/history.actions.ts
var pushUndoHistoryMap = {
	...tablePushUndoHistoryMap,
	...tableColumnPushUndoHistoryMap,
	...relationshipPushUndoHistoryMap,
	...memoPushUndoHistoryMap,
	...settingsPushUndoHistoryMap,
	...editorPushUndoHistoryMap,
	...indexPushUndoHistoryMap,
	...indexColumnPushUndoHistoryMap,
	...tableGroupPushUndoHistoryMap,
	...dictionaryPushUndoHistoryMap
};
var pushStreamHistoryMap = {
	...tablePushStreamHistoryMap,
	...memoPushStreamHistoryMap,
	...settingsPushStreamHistoryMap,
	...tableGroupPushStreamHistoryMap
};
function push(store, history, actions) {
	const undoActions = [];
	const redoActions = [];
	for (const action of actions) {
		const pushUndoHistory = pushUndoHistoryMap[action.type];
		if (!pushUndoHistory) continue;
		pushUndoHistory(undoActions, action, store.state);
		redoActions.push(cloneDeep(action));
	}
	reverseDictionaryListings(undoActions);
	for (const key of Object.keys(pushStreamHistoryMap)) pushStreamHistoryMap[key](undoActions, redoActions, actions);
	if (!undoActions.length || !redoActions.length) return;
	history.push({
		undo: replayDocument(store, undoActions),
		redo: replayDocument(store, redoActions)
	});
}
function cloneAction(version) {
	return (action) => ({
		...cloneDeep(action),
		version
	});
}
var isUnlink = ({ type, payload }) => type === ActionType.changeColumnDomain && payload.value === "";
var unlinkedIdsOf = (actions) => new Set(actions.filter(isUnlink).map(({ payload }) => payload.id));
function endTypeOf(state, actions) {
	const columns = query(state.collections).collection("tableColumnEntities");
	const written = /* @__PURE__ */ new Map();
	for (const { type, payload } of actions) if (type === ActionType.changeColumnDataType) written.set(payload.id, payload.value);
	return (id) => written.get(id) ?? columns.selectById(id)?.dataType ?? "";
}
function liveLinks({ doc, collections }) {
	const columns = query(collections).collection("tableColumnEntities");
	return query(collections).collection("tableEntities").selectByIds(doc.tableIds).flatMap(({ id: tableId, columnIds }) => columns.selectByIds(columnIds).filter(({ domainId }) => domainId !== "").map(({ id, domainId }) => ({
		tableId,
		id,
		value: domainId
	})));
}
function liveColumnIn({ doc, collections }) {
	const tables = query(collections).collection("tableEntities");
	const listed = new Set(doc.tableIds);
	const held = /* @__PURE__ */ new Map();
	return (tableId, id) => {
		if (!listed.has(tableId)) return false;
		const ids = held.get(tableId) ?? new Set(tables.selectById(tableId)?.columnIds);
		held.set(tableId, ids);
		return ids.has(id);
	};
}
function touchedBy(state, actions) {
	const tables = query(state.collections).collection("tableEntities");
	const governed = /* @__PURE__ */ new Map();
	const typed = /* @__PURE__ */ new Map();
	const links = /* @__PURE__ */ new Map();
	const domainIds = new Set(domainTypesOf(actions).keys());
	let loaded = false;
	for (const { type, payload } of actions) if (type === ActionType.changeColumnDomain) links.set(payload.id, payload);
	else if (type === ActionType.changeColumnDataType) typed.set(payload.id, payload.tableId);
	else if (type === ActionType.addColumn) governed.set(payload.id, payload.tableId);
	else if (type === ActionType$2.addTable) for (const id of tables.selectById(payload.id)?.columnIds ?? []) governed.set(id, payload.id);
	else if (type === ActionType$1.loadJson) loaded ||= keepsDictionaries(payload);
	for (const { tableId, id, value } of links.values()) if (value !== "") governed.set(id, tableId);
	if (loaded || domainIds.size) {
		for (const { tableId, id, value } of liveLinks(state)) if (loaded || domainIds.has(value)) governed.set(id, tableId);
	}
	const isLive = liveColumnIn(state);
	const live = (ids) => new Map([...ids].filter(([id, tableId]) => isLive(tableId, id)));
	return {
		governed: live(governed),
		typed: live(typed)
	};
}
function settle(state, { governed, typed }) {
	const columns = query(state.collections).collection("tableColumnEntities");
	const domainTypeOf = domainTypeIn(state, /* @__PURE__ */ new Map());
	const drifted = [];
	const judged = new Map(typed);
	for (const [id, tableId] of governed) {
		const column = columns.selectById(id);
		if (!column?.domainId) continue;
		if (!isListed(state.doc.domainIds, column.domainId)) judged.set(id, tableId);
		else if (column.dataType !== domainTypeOf(column.domainId)) drifted.push({
			tableId,
			id,
			value: column.domainId
		});
	}
	const retyped = drifted.length ? toDomainTypeActions(state, { links: drifted }) : [];
	const typeOf = endTypeOf(state, retyped);
	const unlinked = unlinkedIdsOf(retyped);
	const unlinks = toDomainUnlinkActions(state, [...judged].map(([id, tableId]) => ({
		tableId,
		id,
		value: typeOf(id)
	}))).filter(({ payload }) => !unlinked.has(payload.id));
	return [...retyped, ...unlinks];
}
var replayDocument = (store, actions) => (dispatch, getNextVersion, getState = () => store.state) => {
	dispatch(actions.map(cloneAction(getNextVersion())));
	const state = getState();
	const settled = settle(state, touchedBy(state, actions));
	if (settled.length) dispatch(settled.map(cloneAction(getNextVersion())));
};
var pushHistory = (store, history) => (actions) => {
	safeCallback(push, store, history, actions);
};
var isDictionaryAction = ({ type }) => type.startsWith("dictionary.");
function domainTypesOf(actions) {
	const domainTypes = /* @__PURE__ */ new Map();
	for (const { payload } of actions.filter(isDictionaryAction)) {
		const dataType = payload?.values?.dataType;
		if (payload?.kind === DictionaryKind.domain && isString(dataType)) domainTypes.set(payload.id, dataType);
	}
	return domainTypes;
}
var replayRows = (store, rows, unlinked) => (dispatch, getNextVersion, getState = () => store.state) => {
	const state = getState();
	const held = query(state.collections).collection("tableColumnEntities");
	const restored = unlinked.links.filter(({ link, dataType }) => {
		const column = held.selectById(link.id);
		return column?.domainId === "" && column.dataType === dataType;
	}).map(({ link }) => link);
	const domainTypes = domainTypesOf(rows);
	const columns = toDomainTypeActions(state, {
		domainTypes,
		links: restored
	});
	const typeOf = endTypeOf(state, columns);
	const domainTypeOf = domainTypeIn(state, domainTypes);
	const relinks = restored.filter(({ id, value }) => !isListed(state.doc.domainIds, value) && typeOf(id) === domainTypeOf(value)).map(changeColumnDomainAction);
	const unlinkedIds = unlinkedIdsOf(columns);
	const offRemoved = toDomainUnlinkActions(state, liveLinks(state).filter(({ id, value }) => domainTypes.has(value) && !isListed(state.doc.domainIds, value) && !unlinkedIds.has(id)).map(({ tableId, id }) => ({
		tableId,
		id,
		value: typeOf(id)
	})), { domainTypes });
	const sent = [
		...columns,
		...relinks,
		...offRemoved
	];
	unlinked.links = unlinksOf(state, sent);
	dispatch([...rows, ...sent].map(cloneAction(getNextVersion())));
};
function unlinksOf(state, actions) {
	const columns = query(state.collections).collection("tableColumnEntities");
	const typeOf = endTypeOf(state, actions);
	return actions.filter(isUnlink).flatMap(({ payload: { tableId, id } }) => {
		const value = columns.selectById(id)?.domainId ?? "";
		if (value === "") return [];
		return [{
			link: {
				tableId,
				id,
				value
			},
			dataType: typeOf(id)
		}];
	});
}
function pushSheet(store, history, actions) {
	const rows = actions.filter(isDictionaryAction);
	const undoActions = [];
	const redoActions = [];
	for (const action of rows) {
		const pushUndoHistory = pushUndoHistoryMap[action.type];
		if (!pushUndoHistory) continue;
		pushUndoHistory(undoActions, action, store.state);
		redoActions.push(cloneDeep(action));
	}
	reverseDictionaryListings(undoActions);
	if (!undoActions.length || !redoActions.length) return;
	const unlinked = { links: unlinksOf(store.state, actions) };
	history.push({
		undo: replayRows(store, undoActions, unlinked),
		redo: replayRows(store, redoActions, unlinked)
	});
}
var pushSheetHistory = (store, history) => (actions) => {
	safeCallback(pushSheet, store, history, actions);
};
//#endregion
export { isDictionaryAction, pushHistory, pushSheetHistory, pushStreamHistoryMap, pushUndoHistoryMap };
